import type { CrudOperators } from '@refinedev/core'
import type { CabinetStatus } from '@sirius/contracts/admin'
import type { ColumnFilter } from '@tanstack/react-table'
import type { TFunction } from 'i18next'

/** The UI only toggles active ↔ disabled; decommissioning is not offered. */
export function statusActionFor(status: CabinetStatus, t: TFunction) {
  switch (status) {
    case 'active':
      return {
        label: t('cabinetsPage.status.disable'),
        next: 'disabled',
      } as const
    case 'disabled':
      return { label: t('cabinetsPage.status.enable'), next: 'active' } as const
    case 'decommissioned':
      return null
  }
}

/** A cabinet's status by name (active, disabled, decommissioned). */
export const statusLabel = (status: CabinetStatus, t: TFunction) =>
  t(`cabinetsPage.common.statusValue.${status}`)

/** Select value for "In storage" (`locationId: null`); selects can't hold null. */
export const STORAGE = '__storage__'

export function toLocationId(value: string): string | null {
  return value === STORAGE ? null : value
}

export function fromLocationId(locationId: string | null | undefined): string {
  return locationId ?? STORAGE
}

/**
 * A react-table column filter with its own Refine operator: `@refinedev/react-table`
 * prefers `operator` on the filter over the column's `meta.filterOperator`,
 * so one column can switch between `eq` (a location) and `null` (in storage).
 */
export type OperatorColumnFilter = ColumnFilter & { operator?: CrudOperators }

export const LOCATION_FIELD = 'locationId'

export function locationColumnFilter(value: string): OperatorColumnFilter {
  return value === STORAGE
    ? { id: LOCATION_FIELD, operator: 'null', value: true }
    : { id: LOCATION_FIELD, operator: 'eq', value }
}

/** The selected location option (`STORAGE` or an id), if the column is filtered. */
export function locationFilterValue(
  filters: readonly OperatorColumnFilter[],
): string | undefined {
  const filter = filters.find(({ id }) => id === LOCATION_FIELD)
  if (filter === undefined) {
    return undefined
  }
  if (filter.operator === 'null') {
    return STORAGE
  }
  return typeof filter.value === 'string' ? filter.value : undefined
}
